import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import {
  allShapes,
  freeTextBoxes,
  readPresentation,
  type ShapeInfo,
} from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

/** „Folie 3 und Folie 7“ – für Rückmeldungen, die die Stelle benennen. */
function folienListe(formen: ShapeInfo[]): string {
  const nummern = [...new Set(formen.map((form) => form.slideIndex + 1))].sort((a, b) => a - b);
  if (nummern.length === 1) return `Folie ${nummern[0]}`;
  return `Folie ${nummern.slice(0, -1).join(", ")} und ${nummern[nummern.length - 1]}`;
}

export const lektionPlatzhalterErkennen: Lesson = {
  id: "erkennen",
  title: "Platzhalter oder Textfeld?",
  summary: "Zwei Kästen, die gleich aussehen – und sich völlig unterschiedlich verhalten.",
  durationMinutes: 11,
  learningGoals: [
    "Platzhalter und frei gezogene Textfelder sicher unterscheiden",
    "Die Folgen eines freien Textfelds für Design, Gliederung und Barrierefreiheit benennen",
    "Inhalte aus Textfeldern in die vorgesehenen Platzhalter überführen",
  ],
  steps: [
    {
      id: "unterschied",
      title: "Der Unterschied, den man nicht sieht",
      explanation: [
        "Auf der fertigen Folie sehen beide gleich aus: ein Kasten mit Text. Der " +
          "Unterschied liegt in der Herkunft. Ein Platzhalter kommt aus dem Layout – er " +
          "war schon da, bevor du etwas getippt hast, und trägt die Aufforderung „Titel " +
          "durch Klicken hinzufügen“. Ein Textfeld ziehst du selbst auf, über " +
          "„Einfügen“ → „Textfeld“.",
        "Was daraus folgt, merkt man erst später. Ein Platzhalter kennt seine Rolle: Er " +
          "weiß, dass er der Titel ist. Deshalb erscheint sein Inhalt in der " +
          "Gliederungsansicht, wird von Screenreadern zuerst vorgelesen, übernimmt " +
          "Schriftgröße und Position aus dem Layout und rückt automatisch nach, wenn du " +
          "das Design wechselst.",
        "Ein Textfeld weiß nichts. Es liegt an absoluten Koordinaten auf der Folie, wird " +
          "in der Gliederung nicht angezeigt, taucht beim Export nach Word nicht auf und " +
          "behält seine Schriftgröße auch dann, wenn das ganze Design auf eine andere " +
          "Schrift umgestellt wird. Bei einem Layoutwechsel bleibt es einfach liegen, wo " +
          "es war – notfalls quer über dem neuen Platzhalter.",
        "Das ist die exakte Entsprechung zu „Formatvorlage statt direkter Formatierung“ " +
          "im Word-Kurs. Dort geht es um die Rolle eines Absatzes, hier um die Rolle " +
          "eines Kastens auf der Folie.",
      ],
      keyPoints: [
        "Platzhalter kommen aus dem Layout, Textfelder ziehst du selbst auf.",
        "Nur Platzhalterinhalte erscheinen in der Gliederung und im Export.",
        "Start → Anordnen → Auswahlbereich listet alle Formen einer Folie mit Namen auf.",
      ],
      task: {
        instruction:
          "Prüfe, dass jede deiner Folien überhaupt Platzhalter hat – also über ein " +
          "Layout aufgebaut ist und nicht auf „Leer“ steht.",
        hint: "Start → Layout: Steht dort „Leer“, hat die Folie keine Platzhalter.",
        check: async () => {
          const snapshot = await readPresentation();

          if (snapshot.slides.length === 0) {
            return fail(
              "In der Präsentation ist noch keine Folie vorhanden.",
              "Lege zuerst ein paar Folien an.",
            );
          }
          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar(
              "Die Art einer Form (Platzhalter oder Textfeld)",
              "1.4",
              "Sieh unter „Start“ → „Layout“ nach, ob eine Folie auf „Leer“ steht.",
            );
          }

          const ohne = snapshot.slides.filter(
            (folie) => !folie.shapes.some((form) => form.type === "Placeholder"),
          );

          if (ohne.length === 0) {
            return pass(
              `Alle ${snapshot.slides.length} Folien sind über ein Layout mit Platzhaltern aufgebaut.`,
            );
          }

          return fail(
            `${ohne.length === 1 ? "Eine Folie hat" : `${ohne.length} Folien haben`} überhaupt keinen Platzhalter: ` +
              `Folie ${ohne.map((folie) => folie.index + 1).join(", ")} (Layout „${ohne[0].layoutName ?? "unbekannt"}“).`,
            "Weise der Folie über „Start“ → „Layout“ ein Layout mit Titel und Inhalt zu. Ist die leere Folie bewusst so gewollt – etwa für ein ganzseitiges Bild –, kannst du den Schritt selbst abhaken.",
          );
        },
      },
    },
    {
      id: "keine-textfelder",
      title: "Freie Textfelder aufspüren",
      explanation: [
        "Der schnellste Weg, freie Textfelder zu finden, führt über den Auswahlbereich: " +
          "„Start“ → „Anordnen“ → „Auswahlbereich“. Er listet alle Objekte der aktuellen " +
          "Folie mit Namen auf. Platzhalter heißen dort „Titel 1“, „Inhaltsplatzhalter 2“ " +
          "oder „Untertitel 3“. Freie Textfelder heißen „TextBox 4“ oder „Textfeld 4“.",
        "Der zweite Weg ist die Gliederungsansicht. Text, der auf der Folie steht, aber " +
          "in der Gliederung fehlt, sitzt garantiert in einem freien Textfeld.",
        "Beides zusammen dauert keine Minute – und zeigt bei einer geerbten Präsentation " +
          "meistens sofort, warum sie sich so schwer bearbeiten lässt.",
      ],
      keyPoints: [
        "Auswahlbereich: Platzhalter heißen „Titel“, „Inhaltsplatzhalter“, „Untertitel“.",
        "Ein Objekt namens „Textfeld“ oder „TextBox“ ist ein freier Kasten.",
        "F6 wechselt in Bereichsleisten wie den Auswahlbereich.",
      ],
      task: {
        instruction:
          "Sieh deine Präsentation durch und entferne alle frei gezogenen Textfelder – " +
          "ihr Inhalt gehört in einen Platzhalter.",
        hint: "Auswahlbereich öffnen (Start → Anordnen → Auswahlbereich) und auf Einträge namens „Textfeld“ achten.",
        check: async () => {
          const snapshot = await readPresentation();

          if (snapshot.slides.length === 0) {
            return fail(
              "In der Präsentation ist noch keine Folie vorhanden.",
              "Lege zuerst ein paar Folien an.",
            );
          }
          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar(
              "Die Art einer Form (Platzhalter oder Textfeld)",
              "1.4",
              "Öffne den Auswahlbereich und prüfe selbst, ob dort ein Eintrag namens „Textfeld“ steht.",
            );
          }

          const textfelder = snapshot.slides.flatMap(freeTextBoxes);

          if (textfelder.length === 0) {
            return pass(
              "Kein frei gezogenes Textfeld gefunden – der Inhalt steckt vollständig in Platzhaltern.",
            );
          }

          const mitText = textfelder.filter((form) => form.paragraphs.length > 0);
          const beispiel = mitText[0] ?? textfelder[0];

          return fail(
            `${textfelder.length === 1 ? "Ein freies Textfeld" : `${textfelder.length} freie Textfelder`} gefunden, auf ${folienListe(textfelder)}.` +
              (beispiel.paragraphs.length > 0
                ? ` Auf Folie ${beispiel.slideIndex + 1} steht darin: „${beispiel.paragraphs[0].slice(0, 50)}“.`
                : ""),
            "Text markieren, ausschneiden, ins passende Platzhalterfeld einfügen, dann das leere Textfeld löschen. " +
              "Fehlt auf der Folie ein passender Platzhalter, weise ihr zuerst über „Start“ → „Layout“ ein Layout mit dem passenden Bereich zu.",
          );
        },
      },
    },
    {
      id: "inhalt-im-platzhalter",
      title: "Inhalt gehört in den Platzhalter",
      explanation: [
        "Ein Textfeld zu löschen reicht nicht – der Inhalt muss ja irgendwo hin. Der " +
          "Ablauf ist immer derselbe: Zuerst über „Start“ → „Layout“ ein Layout wählen, " +
          "das einen passenden Bereich mitbringt. Dann den Text ausschneiden und in den " +
          "Platzhalter einfügen. Erst danach das leere Textfeld löschen.",
        "Beim Einfügen lohnt sich der kleine Pfeil unter „Einfügen“ und die Option " +
          "„Nur den Text übernehmen“. Sonst schleppt der Text seine alte Schriftgröße " +
          "und Farbe mit in den Platzhalter – und der ganze Vorteil ist wieder dahin.",
        "Wenn eine Folie mehr Platzhalter hat, als du brauchst, lass die überzähligen " +
          "einfach leer. Leere Platzhalter sind in der Präsentation und im Ausdruck " +
          "unsichtbar; sie zu löschen bringt nichts und macht den Layoutwechsel später " +
          "nur schwieriger.",
      ],
      keyPoints: [
        "Strg + Alt + V öffnet „Inhalte einfügen“ mit der Option „Unformatierten Text“.",
        "Leere Platzhalter sind in der Präsentation unsichtbar – sie dürfen bleiben.",
        "Start → Zurücksetzen holt versehentlich gelöschte Platzhalter zurück.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass auf jeder Folie mit Inhalt der Text in Platzhaltern steht: " +
          "keine freien Textfelder, und mindestens eine Folie mit einem gefüllten " +
          "Inhaltsplatzhalter unter dem Titel.",
        hint: "Der Inhaltsplatzhalter ist der große Bereich unter dem Titel im Layout „Titel und Inhalt“.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar(
              "Die Art einer Form (Platzhalter oder Textfeld)",
              "1.4",
              "Prüfe im Auswahlbereich, ob der Text in Platzhaltern statt in Textfeldern steht.",
            );
          }

          const textfelder = snapshot.slides.flatMap(freeTextBoxes);
          const gefuellteInhalte = allShapes(snapshot).filter(
            (form) =>
              form.type === "Placeholder" &&
              form.paragraphs.length > 0 &&
              !/^(title|titel)\b/i.test(form.name ?? "") &&
              form.placeholder?.type !== "Title" &&
              form.placeholder?.type !== "CenterTitle",
          );

          return fromCriteria(
            [
              criterion(
                textfelder.length === 0
                  ? "Kein frei gezogenes Textfeld vorhanden"
                  : `Noch ${textfelder.length} freie Textfelder auf ${folienListe(textfelder)}`,
                textfelder.length === 0,
              ),
              criterion(
                gefuellteInhalte.length > 0
                  ? `Gefüllte Inhaltsplatzhalter: ${gefuellteInhalte.length}`
                  : "Mindestens ein Inhaltsplatzhalter ist gefüllt",
                gefuellteInhalte.length > 0,
              ),
            ],
            {
              success: "Der Inhalt steckt vollständig in den vorgesehenen Platzhaltern.",
              failure: "Ein Teil des Inhalts hängt noch außerhalb der Platzhalter.",
              hint: "Beim Einfügen in den Platzhalter „Nur den Text übernehmen“ wählen, sonst wandert die alte Formatierung mit.",
            },
          );
        },
      },
    },
  ],
};
